import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Circle } from "lucide-react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";

export default function PortalProjectDetail() {
  const { id } = useParams();
  const { data: p, isLoading } = useQuery({ queryKey: ["portal-project", id], queryFn: async () => (await api.get(`/portal/projects/${id}`)).data, retry: false });

  if (isLoading) return <p className="text-muted-foreground">Loading...</p>;
  if (!p) return <p className="text-muted-foreground">Project not found.</p>;

  return (
    <div>
      <Link to="/dashboard/projects" className="mb-4 inline-flex items-center text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="mr-1.5 h-4 w-4" /> Projects</Link>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-2xl font-bold">{p.title}</h1>
        <div className="flex gap-2"><Badge variant="secondary">{p.status}</Badge><Badge variant="outline">{p.priority}</Badge></div>
      </div>
      <p className="mt-2 text-muted-foreground">{p.description}</p>

      <div className="mt-6 bs-card p-6">
        <div className="flex items-center justify-between text-sm"><span className="font-semibold">Progress</span><span>{p.progress || 0}%</span></div>
        <Progress value={p.progress || 0} className="mt-3 h-2.5" />
      </div>

      <div className="mt-6 grid gap-6 md:grid-cols-2">
        <div className="bs-card p-6">
          <h3 className="font-display font-bold">Milestones</h3>
          <div className="mt-4 space-y-3">
            {(p.milestones || []).map((m, i) => (
              <div key={i} className="flex items-center gap-3 text-sm">
                {m.done ? <CheckCircle2 className="h-5 w-5 text-success" /> : <Circle className="h-5 w-5 text-muted-foreground" />}
                <span className={m.done ? "line-through text-muted-foreground" : ""}>{m.title}</span>
              </div>
            ))}
            {(!p.milestones || p.milestones.length === 0) && <p className="text-sm text-muted-foreground">No milestones yet.</p>}
          </div>
        </div>
        <div className="bs-card p-6">
          <h3 className="font-display font-bold">Latest updates</h3>
          <div className="mt-4 space-y-4">
            {(p.updates || []).map((u, i) => (
              <div key={i} className="border-l-2 border-primary/40 pl-3">
                <p className="text-sm">{u.body}</p>
                <p className="mt-1 text-xs text-muted-foreground">{new Date(u.created_at).toLocaleDateString()}</p>
              </div>
            ))}
            {(!p.updates || p.updates.length === 0) && <p className="text-sm text-muted-foreground">No updates yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
